import { Injectable } from '@angular/core';
import { PacimServiceLineUtilityGroup } from '@core/enumeration';
import { AuthorizationDetailsPacimDTO, AuthorizationDiagnosisPac, AuthorizationProviderDTO, InboundFaxActionWrapper, PACIMProviderAccountPgpsDTO, ReferralSourceInformation } from '@core/model';
import { AuthorizationDetailsPacimService, AuthorizationRequestPacimService, ClinicalInformationPacimService, DiagnosisPacimService, DiagnosisUpdateResult, DocumentPacimService, OrderingProviderPacimService, ServiceLineUtilityService, ServicingFacilitiesPacimService } from '@core/service';
import { AuthDetailsPac, AuthDetailsRequestPac, AuthorizationProduct, AuthorizationProviderPacDTO, AuthorizationReferenceProductDTO, AuthorizationRequestPac, AuthRequestInternalSubmitDataPac, AuthRequestPacCommercialState, AuthRequestPacPayload, DocumentsPAC, FullDocumentPacInterface, OrderingProviderPac } from '@shared/model';
import { PhysicianAccountPgpDTO } from '@mynexus/mynexus-ui-lib';
import { Observable, of, switchMap, take, tap } from 'rxjs';
import { AuthRequestPacCommercialStateManagerService } from './auth-request-pac-commercial-state-manager.service';

@Injectable()
export class AuthRequestPacCommercialFacadeService {
  clinicalInformationId: {[key: string]: string} = {}; // TODO: tech-debt: move to state
  serviceRequestType: AuthorizationReferenceProductDTO;
  private requestTypes: AuthorizationReferenceProductDTO[] = [];

  constructor(
    private requestStateManager: AuthRequestPacCommercialStateManagerService,
    private diagnosisService: DiagnosisPacimService,
    private authRequestService: AuthorizationRequestPacimService,
    private authDetailsService: AuthorizationDetailsPacimService,
    private servicingFacilityService: ServicingFacilitiesPacimService,
    private clinicalInfoService: ClinicalInformationPacimService,
    private documentPacimService: DocumentPacimService,
    private orderingProviderService: OrderingProviderPacimService,
    private serviceLineUtilities: ServiceLineUtilityService
  ) {}

  createInternalAuthRequest(tenantId: string, memberId: string, data: AuthDetailsRequestPac) {
    return this.authRequestService.createInternalAuthorizationRequest(tenantId, memberId, data);
  }

  deleteAuthorization(tenantId: string, authorizationByProductId: string): Observable<void> {
    return this.authRequestService.deleteAuthorization(tenantId, authorizationByProductId);
  }

  getRequestState(): Observable<AuthRequestPacCommercialState> {
    return this.requestStateManager.stateChanges();
  }

  initiateRequest(): void {
    this.clinicalInformationId = {};
    this.requestStateManager.reset();
  }

  prepareForSubmit(tenantId: string, productId: string, lineOfBusinessId? : string | null): void {
    this.getRequestTypes(tenantId, productId, lineOfBusinessId);
  }

  getTabState<T extends keyof AuthRequestPacCommercialState>(tab: T): AuthRequestPacCommercialState[T] {
    return this.requestStateManager.getTabState(tab);
  }

  setTabState<T extends keyof AuthRequestPacCommercialState, Q extends AuthRequestPacCommercialState[T]>(
    tab: T,
    tabState: Q
  ): void {
    this.requestStateManager.setTabState(tab, tabState);
  }

  updateAuthDetails(
    tenantId: string,
    authorizationByProductId: string,
    data: AuthorizationDetailsPacimDTO
  ): Observable<AuthorizationDetailsPacimDTO> {
    return this.authDetailsService.updateAuthorizationDetails(tenantId, authorizationByProductId, data);
  }

  updateDiagnosis(
    tenantId: string,
    authorizationByProductId: string,
    diagnoses: AuthorizationDiagnosisPac[]
  ): Observable<DiagnosisUpdateResult> {
    return this.diagnosisService.updateDiagnosis(tenantId, authorizationByProductId, diagnoses);
  }

  updateServicingFacility(
    tenantId: string,
    authorizationByProductId: string,
    data: AuthorizationProviderDTO
  ): Observable<PACIMProviderAccountPgpsDTO> {
    return this.servicingFacilityService.updateFacilityAccountsByProductId(tenantId, authorizationByProductId, data);
  }

  deleteServicingFacility(tenantId: string, authorizationByProductId: string): Observable<AuthorizationProviderPacDTO> {
    return this.servicingFacilityService.deleteFacilityAccountsByProductId(tenantId, authorizationByProductId);
  }

  saveOrderingProviderInternal(
    tenantId: string,
    authorizationByProductId: string,
    data: OrderingProviderPac
  ): Observable<PhysicianAccountPgpDTO | AuthorizationProviderPacDTO | null> {
    if (data.provider) {
      const provider: AuthorizationProviderDTO = {
        providerAccountId: data.provider.orderingPhysicianAccountId,
        providerPgpId: data.provider.orderingPhysicianPgpId,
      };

      return this.orderingProviderService.updateOrderingProvider(tenantId, authorizationByProductId, provider);
    } else {
      return this.orderingProviderService.deleteOrderingProvider(tenantId, authorizationByProductId);
    }
  }

  sendClinicalInformation(
    tenantId: string,
    authorizationByProductId: string,
    data: unknown,
    facilityType: string
  ): Observable<any> {
    if (this.clinicalInformationId[facilityType]) {
      return this.clinicalInfoService
        .updateClinicalInformation(tenantId, authorizationByProductId, this.clinicalInformationId[facilityType], data);
    } else {
      return this.clinicalInfoService.createClinicalInformation(tenantId, authorizationByProductId, data).pipe(
        tap(res => {
          this.clinicalInformationId[facilityType] = res.clinicalInformationId;
        })
      );
    }
  }

  submitAuthInternal(
    tenantId: string,
    authNumber: string,
    authorizationByProductId: string,
    inboundFaxMetaData?: InboundFaxActionWrapper
  ): Observable<AuthorizationRequestPac> {
    return this.getRequestState().pipe(
      take(1),
      switchMap(state => {
        const contacts: AuthRequestInternalSubmitDataPac = {};

        authorizationByProductId = authorizationByProductId
          ? authorizationByProductId
          : state.authDetails.authorizationProduct.authorizationByProductId;


        if (state.orderingProvider?.physicianContact) {
          contacts.physicianContact = state.orderingProvider.physicianContact;
        }

        const facilityContactNotEmpty = Object.values(state.servicingFacility.facilityContact).some(value => !!value);

        contacts.facilityContact = facilityContactNotEmpty ? state.servicingFacility.facilityContact : null;
        contacts.IsClinicalInformationPresent = !!state.clinicalInformation[state.authDetails?.facilityType.description];
        contacts.hasPreviousCarrierDocuments = state.authDetails.hasPreviousCarrierDocuments;

        const authDetails = state.authDetails;
        const admitDate = authDetails.admitDate;
        const memberCurrentlyAdmitted = authDetails.memberCurrentlyAdmitted;

        contacts.serviceRequestType = this.setRequestType(admitDate, memberCurrentlyAdmitted);
        contacts.referralSourceContact = this.setReferralSourceContactInformation(authDetails);
        contacts.authorizationByProductId = authorizationByProductId;

        if (inboundFaxMetaData) {
          inboundFaxMetaData.authorizationNumber = authNumber;
          contacts.inboundFaxMetaData = inboundFaxMetaData;
        }

        return this.authRequestService.submitInternalAuthorizationRequest(tenantId, authorizationByProductId, contacts);
      })
    );
  }

  submitDocuments(
    documents: DocumentsPAC[],
    tenantId: string,
    authorizationByProductId: string
  ): Observable<FullDocumentPacInterface[]> {
    return this.uploadDocuments(documents, tenantId, authorizationByProductId);
  }

  submitAuthorizationExternal(tenantId: string, memberId: string): Observable<AuthorizationRequestPac> {
    return this.getRequestState().pipe(
      take(1),
      switchMap(state => {
        const authorization = this.mapAuthorizationExternal(state, memberId);

        return this.authRequestService.createExternalAuthorizationRequest(tenantId, memberId, authorization);
      })
    );
  }

  acceptAuthOffer(tenantId: string, memberId: string, authOfferId: string): Observable<null> {
    return this.authRequestService.acceptAuthorizationOffer(tenantId, memberId, authOfferId);
  }

  declineAuthOffer(tenantId: string, memberId: string, authOfferId: string): Observable<null> {
    return this.authRequestService.declineAuthorizationOffer(tenantId, memberId, authOfferId);
  }

  private mapAuthorizationExternal(state: AuthRequestPacCommercialState, memberId: string): AuthRequestPacPayload {
    const { authDetails: { admitDate, memberCurrentlyAdmitted } } = state;

    this.serviceRequestType = this.setRequestType(admitDate, memberCurrentlyAdmitted);

    const authorizationProduct: AuthorizationProduct= {
      memberId: memberId,
      requestReceivedDate: null,
      submittedBy: '',
    };
    const authorization: AuthRequestPacPayload = {
      authorization: {
        authorizationProduct: authorizationProduct,
        ...state.authDetails,
      },
      diagnoses: state.diagnoses,
      documents: state.documents.documentsList || [],
      clinicalInformation: state.clinicalInformation[state.authDetails?.facilityType.description],
      facilityContact: state.servicingFacility.facilityContact,
      facility: null,
      physician: null,
      serviceRequestType: this.serviceRequestType,
      referralSourceContact: this.setReferralSourceContactInformation(state.authDetails),
    };

    if (state.servicingFacility.provider) {
      authorization.facility = {
        providerAccountId: state.servicingFacility.provider.servicingProviderAccountId,
        providerPgpId: state.servicingFacility.provider.servicingProviderPgpId,
      };
    }

    if (state.orderingProvider.physicianContact) {
      authorization.physicianContact = { ...state.orderingProvider.physicianContact };
    } else {
      authorization.physician = {
        providerAccountId: state.orderingProvider.provider.orderingPhysicianAccountId,
        providerPgpId: state.orderingProvider.provider.orderingPhysicianPgpId,
      };
    }

    return authorization;
  }

  private uploadDocuments(
    documents: DocumentsPAC[],
    tenantId: string,
    authorizationByProductId: string
  ): Observable<FullDocumentPacInterface[]> {
    const newDocuments = documents?.length === 0
      ? documents
      : documents.filter(item => !item.docMetaDataIDs?.length || item.docMetaDataIDs?.length === 0);

    return this.saveDocuments(tenantId, authorizationByProductId, newDocuments).pipe(
      tap(uploadDocuments => {
        this.setStateForDocuments(documents, uploadDocuments);
      })
    );
  }

  deleteDocuments(documents: DocumentsPAC[] = [], tenantId: string, authorizationByProductId: string): Observable<null> {
    return this.getRequestState().pipe(
      take(1),
      switchMap(state => {
        const savedDocumentsIds = state.documents?.savedDocumentsIds;

        if (savedDocumentsIds?.length > 0) {
          const selectedDocumentsIds = documents?.map(item => item?.docMetaDataIDs).flat() || [];
          const removedDocumentsIds = savedDocumentsIds.filter(id => !selectedDocumentsIds.includes(id));

          if (removedDocumentsIds?.length > 0) {
            const documentsIds = savedDocumentsIds.filter(id => !removedDocumentsIds.includes(id));

            return this.removeDocuments(tenantId, authorizationByProductId, removedDocumentsIds).pipe(
              tap(() => {
                this.setTabState('documents', { documentsList: documents, savedDocumentsIds: documentsIds });
              })
            );
          }

          return of(null);
        }

        return of(null);
      })
    );
  }

  private setReferralSourceContactInformation(authDetails: AuthDetailsPac): ReferralSourceInformation {
    const contactName = authDetails.referralSourceContactName;
    const phoneNumber = authDetails.referralSourcePhone;
    const faxNumber = authDetails.referralSourceFax;

    return contactName || phoneNumber || faxNumber
      ? {
        contactName,
        phoneNumber,
        faxNumber,
      }
      : null;
  }

  private setRequestType(admitDateValue: string, memberCurrentlyAdmitted: boolean): AuthorizationReferenceProductDTO {
    const currentDate = new Date();
    const admitDate = new Date(admitDateValue?.replace('Z', ''));

    currentDate.setHours(0, 0, 0, 0);
    admitDate.setHours(0, 0, 0, 0);

    const isDateEqual = admitDate.getTime() ===  currentDate.getTime();

    if (admitDate > currentDate || (isDateEqual && !memberCurrentlyAdmitted)) {
      return this.requestTypes.find(option => option.description === 'Prior');
    }

    if ((admitDate < currentDate || isDateEqual) && memberCurrentlyAdmitted) {
      return this.requestTypes.find(option => option.description === 'Concurrent');
    }

    if (admitDate < currentDate && !memberCurrentlyAdmitted) {
      return this.requestTypes.find(option => option.description === 'Retro');
    }

    return null;
  }

  private setStateForDocuments(selectedDocuments: DocumentsPAC[], uploadedDocuments: FullDocumentPacInterface[]): void {
    let documents: DocumentsPAC[];

    if (uploadedDocuments?.length > 0) {
      documents = selectedDocuments.map(file => {
        if (!file.docMetaDataIDs?.length) {
          file.docMetaDataIDs = uploadedDocuments
            .filter(item => item.documentFileName === file.documentFileName)
            .map(item => item.docMetaDataID);
        }

        return file;
      });
    } else {
      documents = selectedDocuments;
    }

    const documentsIdsList = documents.map(item => item.docMetaDataIDs).flat();

    this.setTabState('documents', { documentsList: documents, savedDocumentsIds: documentsIdsList });
  }

  private saveDocuments(
    tenantId: string,
    authorizationByProductId: string,
    files: DocumentsPAC[]
  ): Observable<FullDocumentPacInterface[]> {
    return this.documentPacimService.uploadFilesRequestInitial(tenantId, authorizationByProductId, files);
  }

  private removeDocuments(tenantId: string, authorizationByProductId: string, fileIds: string[]): Observable<null> {
    return this.documentPacimService.deleteFilesRequestInitial(tenantId, authorizationByProductId, fileIds);
  }

  private getRequestTypes(tenantId: string, productId: string, lineOfBusinessId? : string | null): void {
    this.serviceLineUtilities.getAllUtility(tenantId, productId, lineOfBusinessId).pipe(take(1)).subscribe(data => {
      this.requestTypes = data[PacimServiceLineUtilityGroup.RequestType];
    });
  }

  // Transfer Type State Management - Delegated to AuthorizationRequestPacimService

  /**
   * Set the selected transfer type.
   */
  set selectedTransferType(transferType: AuthorizationReferenceProductDTO | null) {
    this.authRequestService.selectedTransferType = transferType;
  }

  /**
   * Get the current selected transfer type value synchronously.
   */
  get selectedTransferType(): AuthorizationReferenceProductDTO | null {
    return this.authRequestService.selectedTransferType;
  }

  /**
   * Observable stream of the selected transfer type.
   */
  get selectedTransferType$(): Observable<AuthorizationReferenceProductDTO | null> {
    return this.authRequestService.selectedTransferType$;
  }

  /**
   * Get and clear the selected transfer type in one operation.
   */
  consumeSelectedTransferType(): AuthorizationReferenceProductDTO | null {
    return this.authRequestService.consumeSelectedTransferType();
  }
}
